import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import * as notificacionesApi from '@/modules/notificaciones/api/notificacionesApi'
import type { FiltrosNotificaciones } from '@/modules/notificaciones/types'

// ============================================================================
// Query Keys
// ============================================================================

export const notificacionesKeys = {
  all: ['notificaciones'] as const,
  dropdown: () => [...notificacionesKeys.all, 'dropdown'] as const,
  historial: () => [...notificacionesKeys.all, 'historial'] as const,
  historialFiltered: (filters?: FiltrosNotificaciones) =>
    [...notificacionesKeys.historial(), { filters }] as const,
  stats: () => [...notificacionesKeys.all, 'stats'] as const,
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook para el dropdown del navbar.
 * Hace polling cada 60 segundos para mantener el badge actualizado.
 */
export function useNotificacionesDropdown() {
  return useQuery({
    queryKey: notificacionesKeys.dropdown(),
    queryFn: notificacionesApi.getDropdownNotificaciones,
    refetchInterval: 60_000, // Polling cada 60s
    staleTime: 30_000,       // Considerar datos frescos por 30s
  })
}

/**
 * Hook para el historial paginado con filtros.
 */
export function useNotificacionesHistorial(filters?: FiltrosNotificaciones) {
  return useQuery({
    queryKey: notificacionesKeys.historialFiltered(filters),
    queryFn: () => notificacionesApi.getHistorialNotificaciones(filters),
  })
}

/**
 * Hook para las estadísticas.
 */
export function useNotificacionesStats() {
  return useQuery({
    queryKey: notificacionesKeys.stats(),
    queryFn: notificacionesApi.getStatsNotificaciones,
  })
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Mutation para marcar una notificación como leída.
 * Invalida dropdown, historial y stats después del éxito.
 */
export function useMarcarComoLeida() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificacionId: number) =>
      notificacionesApi.marcarComoLeida(notificacionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificacionesKeys.dropdown() })
      queryClient.invalidateQueries({ queryKey: notificacionesKeys.historial() })
      queryClient.invalidateQueries({ queryKey: notificacionesKeys.stats() })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al marcar notificación')
    },
  })
}

/**
 * Mutation para marcar todas como leídas.
 * Invalida dropdown, historial y stats después del éxito.
 */
export function useMarcarTodasLeidas() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: notificacionesApi.marcarTodasLeidas,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: notificacionesKeys.dropdown() })
      queryClient.invalidateQueries({ queryKey: notificacionesKeys.historial() })
      queryClient.invalidateQueries({ queryKey: notificacionesKeys.stats() })
      toast.success(data.message)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al marcar notificaciones')
    },
  })
}
